import { normalizeBlur } from '@signa/shared';
import { byOrder, toBlurMask, toClipPath } from './layout.js';
/**
 * ステージのスタイル。
 *
 * **`flex: 0 0 auto` を明示する**(PoC 申し送り 4)。既定では flex の子が
 * 縮められ、座標系と実表示がずれる —— PoC で実際に踏んだ問題。
 */
const STAGE_STYLE = `
  position: relative;
  flex: 0 0 auto;
  overflow: hidden;
  line-height: 0;
`;
const LAYER_STYLE = `
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;
export function createStage(container) {
    const root = document.createElement('div');
    root.className = 'signa-stage';
    root.setAttribute('style', STAGE_STYLE);
    const background = document.createElement('img');
    background.className = 'signa-bg';
    background.setAttribute('style', `${LAYER_STYLE} object-fit: fill;`);
    // 背景は装飾で、内容の説明は埋め込み先の文脈が持つ。
    background.alt = '';
    background.decoding = 'async';
    const canvas = document.createElement('canvas');
    canvas.className = 'signa-gl';
    canvas.setAttribute('style', LAYER_STYLE);
    const masks = document.createElement('div');
    masks.className = 'signa-masks';
    // マスクは見えない要素なので、ポインタイベントを奪わせない。
    masks.setAttribute('style', `${LAYER_STYLE} pointer-events: none;`);
    root.append(background, canvas, masks);
    /*
     * **入れ物の先頭に差し込む**(#184)。ステージは常に最背面に置く。
     *
     * 呼び出し側は同じ入れ物に自前の層を重ねる —— S04 は編集ハンドルの SVG を
     * **兄弟として**置いており、どちらも `z-index` を持たないため
     * **DOM の順序がそのまま重なり順**になる。
     *
     * `append` だと、**既にオーバーレイがある状態で作り直したときに
     * ステージがその上に載る** —— 図形の輪郭も頂点ハンドルも canvas の下に
     * 潜って見えなくなり、クリックまで canvas に奪われる。
     * 「障害物を置いても出てこない。入り直すと直る」の正体がこれだった。
     *
     * **先頭に入れる規則にすれば、いつ作り直しても重なり順は変わらない。**
     * ビューアは描画コア専用の入れ物に載せるので、どちらでも結果は同じ。
     */
    container.prepend(root);
    return { root, background, canvas, masks };
}
/** ステージの表示サイズを反映する。 */
export function applyTransform(stage, transform) {
    stage.root.style.width = `${String(transform.width)}px`;
    stage.root.style.height = `${String(transform.height)}px`;
}
/** 背景画像を差し替える。`null` で消す。 */
export function setBackground(stage, url) {
    if (url === null) {
        stage.background.removeAttribute('src');
        stage.background.style.display = 'none';
        return;
    }
    stage.background.style.display = '';
    if (stage.background.getAttribute('src') !== url)
        stage.background.src = url;
}
/**
 * 障害物マスクを組み直す。
 *
 * **背景画像を障害物の形に切り抜いて動画より手前に重ねる**(設計書 8.4 /
 * PoC 3.6)。動画を隠すのではなく「背景が手前にある」ように見せる方式なので、
 * 柱の向こうを動画が通り過ぎる表現になる。
 */
export function renderMasks(stage, obstacles, transform, backgroundUrl, fit) {
    stage.masks.replaceChildren();
    // 輪郭 SVG は masks の直前に差し込まれる。作り直す前に消す。
    stage.root.querySelectorAll('svg').forEach((el) => {
        el.remove();
    });
    if (backgroundUrl === null)
        return;
    for (const obstacle of byOrder(obstacles)) {
        const mask = document.createElement('div');
        // 背景画像そのものを切り抜く。別画像を重ねるのではないので、
        // 元の見た目と完全に一致する。
        mask.setAttribute('style', maskStyle(obstacle.points, transform, backgroundUrl, fit, normalizeBlur(obstacle.blur)));
        stage.masks.append(mask);
    }
}
/**
 * マスク要素のスタイルを組み立てる。
 *
 * **`blur` はキャンバス幅に対する比率**(#125 / [ADR-0034](../../../docs/adr/0034-obstacle-edge-blur.md))。
 * **`normalizeBlur` を通した値を渡す**(範囲と丸めはそこで担保する)。
 * ここで**表示 px に換算する** —— 埋め込み先の幅が違っても
 * 同じ見え方になるのは、この換算が描画コアの側にあるから
 * (S04 / V01 / V02 の 3 者共有 —— 設計書 7.4)。
 *
 * **ぼかし 0 は従来どおり `clip-path`。** マスク画像は `clip-path` と違って
 * ラスタライズが要るので、**コストを払うのは実際にぼかす障害物だけ**にする。
 * 既存プロジェクトは 1 つも払わない。
 *
 * DOM を触らない純粋な関数なので単体テストできる([ADR-0019](../../../docs/adr/0019-dev-foundation.md) の方針)。
 */
export function maskStyle(points, transform, backgroundUrl, fit, blur) {
    const stdDeviation = blur * transform.width;
    return [
        LAYER_STYLE,
        `background-image: url("${cssUrl(backgroundUrl)}");`,
        `background-size: ${fit === 'cover' ? 'cover' : '100% 100%'};`,
        'background-repeat: no-repeat;',
        ...(stdDeviation > 0
            ? blurMaskDeclarations(points, transform, stdDeviation)
            : [`clip-path: ${toClipPath(points, transform)};`]),
        'pointer-events: none;',
    ].join(' ');
}
/**
 * ぼかしたマスクの宣言。
 *
 * **`-webkit-` を併記する。** 無接頭辞の `mask-*` は Safari 15.4 以降で、
 * それ未満は `-webkit-mask-*` しか解さない —— **接頭辞側だけを見る環境で
 * マスクが効かないと、障害物が消えて動画が素通しになる**(隠すのではなく
 * 「背景が手前にある」ように見せる方式のため)。
 *
 * **要素に `filter` は付けない。** 付けた瞬間に切り抜いた写真の中身まで
 * ぼける —— ぼかすのは輪郭だけ、が ADR-0034 の要件。
 */
function blurMaskDeclarations(points, transform, stdDeviation) {
    const url = `url("${toBlurMask(points, transform, stdDeviation)}")`;
    return [
        `-webkit-mask-image: ${url};`,
        '-webkit-mask-size: 100% 100%;',
        '-webkit-mask-repeat: no-repeat;',
        `mask-image: ${url};`,
        'mask-size: 100% 100%;',
        'mask-repeat: no-repeat;',
    ];
}
/**
 * CSS の `url()` に埋め込める形にする。
 *
 * **署名付き URL はクエリに任意の文字を含む。** `"` や `)` が素通りすると
 * スタイルが壊れる(値の注入経路にもなる)。
 *
 * DOM を触らない純粋な関数なので単体テストできる(ADR-0019 の方針)。
 */
export function cssUrl(url) {
    return url.replace(/["'()\\\s]/g, (c) => `\\${c}`);
}
const SVG_NS = 'http://www.w3.org/2000/svg';
/**
 * 輪郭の SVG に付ける印。**作り直すときに自分だけを消す**ために要る。
 *
 * `renderMasks` はステージ内の SVG を種類を問わず消すが、**割り当てが
 * 変わったときはマスクを組み直さない**(#208)—— そちらの経路では
 * この印を頼りに前の輪郭を外す。
 */
const OUTLINES_CLASS = 'signa-outlines';
/**
 * 輪郭を SVG で描く。
 *
 * **2 つの用途がある。**
 *
 * | 呼ぶ人 | 描くもの | なぜ |
 * |---|---|---|
 * | [`FallbackRenderer`](./fallback-renderer.ts) | **枠ぜんぶ** | WebGL2 非対応環境(#33)。動画は載せられないが「どこに何が入るか」は伝わる。選択中の枠を示すのにも使う(S04) |
 * | [`WebGlRenderer`](./webgl-renderer.ts) | **素材が入っていないセル**(#208) | 取引先に渡す画面で「どこに」「何を」できるか伝える。埋めたセルの枠は消える |
 *
 * **座標は呼び出し側が表示ピクセルに変換して渡す。** セルの輪郭は
 * [`cellOutline`](./hit-test.ts) が表示座標で返すため、ここで正規化座標を
 * 受けると**2 つの座標系が混ざる。** 並べ替えも呼び出し側の責務
 * ([`byOrder`](./layout.ts))。
 */
export function renderOutlines(stage, shapes, transform, highlighted = new Set()) {
    /*
     * **前の輪郭を消してから作る。**
     *
     * [`renderMasks`](#renderMasks) はステージ内の SVG を全部消すので、
     * そこを通る経路(`resize`)では消えている。**が、割り当てが変わったときは
     * マスクを組み直さない**(#208)—— 消さずに追加すると**素材を入れ替える
     * たびに SVG が積み重なる。**
     */
    stage.root.querySelectorAll(`.${OUTLINES_CLASS}`).forEach((el) => {
        el.remove();
    });
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', OUTLINES_CLASS);
    svg.setAttribute('style', `${LAYER_STYLE} pointer-events: none;`);
    svg.setAttribute('viewBox', `0 0 ${String(transform.width)} ${String(transform.height)}`);
    for (const shape of shapes) {
        const polygon = document.createElementNS(SVG_NS, 'polygon');
        polygon.setAttribute('points', shape.points.map(toPointPair).join(' '));
        polygon.setAttribute('fill', 'rgba(255, 255, 255, 0.08)');
        // 埋め込み先が調整できる口(#33 が公開を認めた 3 つのうちの 1 つ)。
        polygon.setAttribute('stroke', 'var(--signa-accent, #4da3ff)');
        polygon.setAttribute('stroke-width', highlighted.has(shape.id) ? '3' : '1.5');
        polygon.setAttribute('stroke-dasharray', '6 4');
        svg.append(polygon);
        if (shape.label !== undefined) {
            const { at, hint } = shape.label;
            /*
             * **2 つとも置く**(#208)。どちらを見せるかは**CSS が決める** ——
             * `(hover: none) and (pointer: coarse)` で切り替える。
             *
             * **JS で判定して片方だけ置かない。** タッチ対応のノート PC を
             * 誤判定するうえ、判定した時点の状態で固定されてしまう。
             */
            svg.append(hintText(at, hint.drop, 'signa-outline-hint-drop'), hintText(at, hint.touch, 'signa-outline-hint-touch'));
        }
    }
    stage.masks.before(svg);
}
/**
 * 枠の中の案内 1 つ。
 *
 * **見た目は CSS に委ねる**(色・字の大きさ・出し分け)。描画コアは
 * V01 / V02 / S04 が共有するので、**スタイルを抱えると埋め込み先で
 * 調整できなくなる**(#33 が公開を認めた口の考え方と同じ)。
 */
function hintText(at, text, variant) {
    const element = document.createElementNS(SVG_NS, 'text');
    element.setAttribute('class', `signa-outline-hint ${variant}`);
    element.setAttribute('x', String(at[0]));
    element.setAttribute('y', String(at[1]));
    element.setAttribute('text-anchor', 'middle');
    element.setAttribute('dominant-baseline', 'central');
    element.textContent = text;
    return element;
}
function toPointPair([x, y]) {
    return `${String(x)},${String(y)}`;
}
//# sourceMappingURL=stage.js.map